import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useLocaleNavigate from 'utils/customHooks/useLocaleNavigate';
import { FiHome } from 'react-icons/fi';
import { BiStore } from 'react-icons/bi';
import './cart.scss';
//translations
import { useTranslation } from 'react-i18next';
//zustand
import { useUserStore, useCompanyStore, useCartStore } from 'stores';
// Utils-Zustand
import { calculateShippingCosts, calculateShippingFee } from 'components/cart/utils';
//api
import { saveChanges } from 'api/users';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
//components
import { InputCustom } from 'components/input';
import OrderSummary from 'components/cart/OrderSummary';
import CheckoutLayout from 'components/checkout/CheckoutLayout';
import CheckoutStepper from 'components/checkout/CheckoutStepper';
import CheckoutHeader from 'components/checkout/CheckoutHeader';
import CheckoutOptionCard from 'components/checkout/CheckoutOptionCard';
import CheckoutNav from 'components/checkout/CheckoutNav';
import CheckoutHelpBox from 'components/checkout/CheckoutHelpBox';
import CustomTab from 'components/Tab';
import LoginComp from 'components/login/LoginComp';
import RegisterComp from 'components/register/RegisterComp';
import AddressForm from 'components/addressForm';
import OrderPickup from 'components/cart/OrderPickup';
import { goUp } from 'helpers';

const Shipping = () => {
  const { t } = useTranslation(['shipping', 'backoffice', 'cart']);
  const navigate = useLocaleNavigate();
  const { assignedLibrary, libraryPath } = useAssignedLibrary();

  // Zustand stores
  const {
    name: empresa,
    id: bookstoreId,
    enabled_countries,
    shipping_cost,
    shipping_cost_islands,
    min_purchase_for_free_shipping,
  } = useCompanyStore();

  const {
    isLogged,
    shippingAddress,
    firstName,
    lastName,
    email,
    phoneNumber,
    id,
    isConcourseUser,
    setShippingAddress,
    setFirstName,
    setLastName,
    setPickUpLibrary,
  } = useUserStore();
  const isConcourseActive = isConcourseUser === true || isConcourseUser === 1 || isConcourseUser === 'true';

  const {
    countItems,
    total,
    isShippingAvailable,
    errorAmountPrice,
    isStorePickup,
    contactInfo,
    setIsStorePickup,
    setShippingFee,
    setShippingCartAddress,
  } = useCartStore();

  const [showDeliveryOptions, setShowDeliveryOptions] = useState(true);
  const [isGift] = useState(false);
  const [validData, setValidData] = useState([false, t('completeFields')]);
  const [giftInfo, setGiftInfo] = useState({
    email: '',
    name: '',
    surname: '',
    message: '',
  });
  const [deliveryMode, setDeliveryMode] = useState(null);

  const initialShippingValue = () => {
    if (shippingAddress) {
      return {
        ...shippingAddress,
        email: email || '',
        first_name: firstName || '',
        last_name: lastName || '',
        phone: shippingAddress.phone || phoneNumber || '',
      };
    } else {
      return {
        address: '',
        postal_code: '',
        first_name: firstName || '',
        last_name: lastName || '',
        locality: '',
        province: '',
        country: '',
        email: email || '',
        phone: phoneNumber || '',
      };
    }
  };

  const cartItems = useCartStore((state) => state.items);
  const [updateDataShipping, setUpdateDataShipping] = useState(() => initialShippingValue());
  const shippingCartAddress = useCartStore((state) => state.shippingCartAddress);

  useEffect(() => {
    window.document.title = 'Carrito de compras | ' + empresa;
    if (!countItems) navigate('/');
    goUp();
    setIsStorePickup(false);
    setPickUpLibrary(null);
    setShippingCartAddress(updateDataShipping);
  }, [
    countItems,
    empresa,
    navigate,
    setIsStorePickup,
    setPickUpLibrary,
    setShippingCartAddress,
    updateDataShipping,
  ]);

  useEffect(() => {
    if (isLogged && isConcourseActive) {
      setShowDeliveryOptions(false);
      setDeliveryMode('homeDelivery');
      setIsStorePickup(false);
      setPickUpLibrary(null);
    }
  }, [isLogged, isConcourseActive, setIsStorePickup, setPickUpLibrary]);

  useEffect(() => {
    const newShippingCost = calculateShippingFee(
      shippingCartAddress?.province?.label || shippingCartAddress?.province || '',
      cartItems,
      shipping_cost,
      shipping_cost_islands
    );
    const calculatedShipping = calculateShippingCosts(
      newShippingCost,
      min_purchase_for_free_shipping,
      total,
      isConcourseActive
    );
    setShippingFee(calculatedShipping);
  }, [
    shippingCartAddress?.province,
    cartItems,
    total,
    min_purchase_for_free_shipping,
    setShippingFee,
    shipping_cost,
    shipping_cost_islands,
    isConcourseActive,
  ]);

  const checkData = (updatingAddressToCheck) => {
    if (updatingAddressToCheck !== undefined) {
      const properties = ['first_name', 'address', 'postal_code', 'locality', 'phone'];
      for (const prop of properties) {
        if (updatingAddressToCheck[prop] === undefined || updatingAddressToCheck[prop] === '') {
          setValidData([false, t('completeFields')]);
          return;
        }
      }
      setValidData([
        true,
        `${updatingAddressToCheck.address}, ${updatingAddressToCheck.postal_code}, ${updatingAddressToCheck.locality}`,
      ]);
    }
  };

  useEffect(() => {
    if (isLogged) {
      checkData(updateDataShipping);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDataShipping, isLogged]);

  const handleChangeSave = async (values) => {
    const payload = {
      first_name: values?.first_name,
      last_name: values?.last_name,
      email: values?.email,
      phone: values?.phone,
      address: values?.address,
      country: values?.country,
      province: values?.province?.label || values?.province || '',
      locality: values?.locality,
      postal_code: values?.postal_code,
    };
    try {
      const changes = await saveChanges(
        {
          shipping_address: payload,
          first_name: values?.first_name,
          last_name: values?.last_name,
        },
        id
      );
      if (changes.status === 200) {
        payload.province = values?.province || {};
        setShippingAddress(payload);
        // Actualizar también el nombre y apellido en el store del usuario
        if (values?.first_name) {
          setFirstName(values.first_name);
        }
        if (values?.last_name) {
          setLastName(values.last_name);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleTabChange = (key) => {
    const currentProvince = shippingCartAddress?.province?.label || shippingCartAddress?.province || '';
    if (key === 'homeDelivery') {
      setIsStorePickup(false);
      setPickUpLibrary(null);
      if (!currentProvince) return;
      const calculatedShippingCost = calculateShippingFee(
        currentProvince,
        cartItems,
        shipping_cost,
        shipping_cost_islands
      );
      const finalShippingCost = calculateShippingCosts(
        calculatedShippingCost,
        min_purchase_for_free_shipping,
        total,
        isConcourseActive
      );
      setShippingFee(finalShippingCost);
    } else if (key === 'storePickup') {
      setIsStorePickup(true);
      setPickUpLibrary(bookstoreId);
      setShippingFee(0);
    }
  };

  const selectDeliveryMode = (mode) => {
    setDeliveryMode(mode);
    handleTabChange(mode);
    setShowDeliveryOptions(false);
  };

  const goBackToDeliveryOptions = () => {
    setShowDeliveryOptions(true);
    setDeliveryMode(null);
    setIsStorePickup(false);
    setPickUpLibrary(null);
  };

  const disableNext = () => {
    if (!isLogged || !isShippingAvailable?.availability || errorAmountPrice || !validData[0]) {
      return true;
    }
    return false;
  };

  const nextDisabled =
    showDeliveryOptions ||
    (disableNext() && !isStorePickup) ||
    (isStorePickup && contactInfo.email === '');

  const goToPayment = useCallback(() => {
    if (!nextDisabled) {
      navigate(`/${assignedLibrary}/pago`);
      goUp();
    }
  }, [assignedLibrary, navigate, nextDisabled]);

  const renderGiftSection = () =>
    isGift && (
      <>
        <div className="mt-4">
          <h2 className="checkout-card__title">{t('recipientData')}</h2>
          <div className="giftDiv">
            <Row>
              <Col md={6}>
                <p className="mb-0">{t('recipientName')}</p>
                <InputCustom
                  onChange={(e) => setGiftInfo({ ...giftInfo, name: e.target.value })}
                  type="text"
                />
              </Col>
              <Col md={6}>
                <p className="mb-0">{t('recipientLastName')}</p>
                <InputCustom
                  onChange={(e) => setGiftInfo({ ...giftInfo, surname: e.target.value })}
                  type="text"
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={12}>
                <p className="mb-0">{t('recipientEmail')}</p>
                <InputCustom
                  onChange={(e) => setGiftInfo({ ...giftInfo, email: e.target.value })}
                  type="text"
                />
                <p className="mb-0">Mensaje</p>
                <InputCustom
                  onChange={(e) => setGiftInfo({ ...giftInfo, message: e.target.value })}
                  type="textarea"
                />
              </Col>
            </Row>
          </div>
        </div>
        <p className="textInfo mt-4">{t('contactDataUseWarning')}</p>
        <p className="textInfo my-4">
          {t('giftNotification')}
          <Link to={`/${assignedLibrary}/faqs`}>{t('faq')}</Link> {t('our')}
          <Link to={`/${assignedLibrary}/aviso-legal-y-politica-de-privacidad`}>
            {t('dataPrivacyPolicy')}
          </Link>{' '}
          {t('or')}
          <Link to={`/${assignedLibrary}/contacto`}>{t('contactUs')}</Link>
        </p>
      </>
    );

  const renderDeliveryBackLink = () => (
    !isConcourseActive && (
      <button type="button" className="checkout-delivery-back" onClick={goBackToDeliveryOptions}>
        ← {t('declineDeliveryOption', { ns: 'cart' })}
      </button>
    )
  );

  const renderHomeDeliveryPanel = () => (
    <div className="checkout-card">
      <h2 className="checkout-card__title">{t('addressAndDeliveryData')}</h2>
      <p className="checkout-card__subtitle">{t('deliverySectionDesc', { ns: 'cart' })}</p>
      <AddressForm
        data={updateDataShipping}
        onChange={(newData) => {
          setUpdateDataShipping(newData);
          setShippingCartAddress(newData);
        }}
        onSave={() => handleChangeSave(updateDataShipping)}
        countries={enabled_countries}
        isShippingAddressDetails
        hideCancelButton
      />
      {renderDeliveryBackLink()}
    </div>
  );

  const renderStorePickupPanel = () => (
    <div className="checkout-card">
      <p className="checkout-card__eyebrow mb-1 d-flex justify-content-center">{t('requiredStep', { ns: 'cart' })}</p>
      <h2 className="checkout-card__title">{t('storePickupShort', { ns: 'cart' })}</h2>
      <p className="checkout-card__subtitle">{t('storePickupDesc', { ns: 'cart' })}</p>
      <OrderPickup />
      {renderDeliveryBackLink()}
    </div>
  );

  const renderLoggedInDelivery = () => (
    <>
      {showDeliveryOptions && !isConcourseActive && (
        <div className="checkout-card checkout-card--centered">
          <p className="checkout-card__eyebrow">{t('requiredStep', { ns: 'cart' })}</p>
          <h2 className="checkout-card__title">{t('selectDeliveryTitle', { ns: 'cart' })}</h2>
          <p className="checkout-card__subtitle">{t('selectDeliverySubtitle', { ns: 'cart' })}</p>
          <div className="checkout-options-grid">
            <CheckoutOptionCard
              icon={<FiHome />}
              title={t('homeDelivery')}
              description={t('homeDeliveryDesc', { ns: 'cart' })}
              accent="orange"
              isActive={deliveryMode === 'homeDelivery'}
              onSelect={() => selectDeliveryMode('homeDelivery')}
            />
            {/* <CheckoutOptionCard
              icon={<BiStore />}
              title={t('storePickupShort', { ns: 'cart' })}
              description={t('storePickupDesc', { ns: 'cart' })}
              highlightText={t('storePickupFree', { ns: 'cart' })}
              accent="blue"
              isActive={deliveryMode === 'storePickup'}
              onSelect={() => selectDeliveryMode('storePickup')}
            /> */}
          </div>
        </div>
      )}

      {(!showDeliveryOptions || isConcourseActive) &&
        (deliveryMode === 'homeDelivery' || isConcourseActive) &&
        renderHomeDeliveryPanel()}
      {!showDeliveryOptions && deliveryMode === 'storePickup' && renderStorePickupPanel()}

      {renderGiftSection()}
    </>
  );

  const renderGuestAuth = () => (
    <div className="checkout-login-tabs">
      <h2 className="checkout-card__title my-3">
        {t('areYouUserOf')} {empresa}?
      </h2>
      <CustomTab
        customClass="custom-tab"
        onSelect={handleTabChange}
        tabs={[
          { title: t('login'), id: 'login', renderContent: () => <LoginComp fromCarro /> },
          {
            title: t('register'),
            id: 'register',
            renderContent: () => (
              <div className="m-3">
                <RegisterComp />
              </div>
            ),
          },
        ]}
      />
      {renderGiftSection()}
    </div>
  );

  return (
    <CheckoutLayout sidebar={total > 0 && <OrderSummary activeStep={1} />}>
      <CheckoutStepper activeStep={1} />
      <CheckoutHeader step={2} title={t('deliveryMethod')} />

      {isLogged ? renderLoggedInDelivery() : renderGuestAuth()}

      <CheckoutNav
        backButton={
          <Button
            className="checkout-btn checkout-btn--ghost"
            as={Link}
            to={libraryPath('carrito')}
          >
            ← {t('goBack')}
          </Button>
        }
        nextButton={
          showDeliveryOptions ? null : nextDisabled ? (
            <Button className="checkout-btn checkout-btn--primary disabled" disabled>
              {t('following', { ns: 'cart' })} →
            </Button>
          ) : (
            <Button className="checkout-btn checkout-btn--primary" onClick={goToPayment}>
              {t('following', { ns: 'cart' })} →
            </Button>
          )
        }
      />

      <CheckoutHelpBox />
    </CheckoutLayout>
  );
};

export default Shipping;
